import { createSlice } from '@reduxjs/toolkit';
import _ from 'lodash';
import Safe from "../artifacts/Safe.json";
import OriginationManager from "../artifacts/OriginationManager.json";
import { ZERO_ADDRESS, EMPTY_DATA, SALT, FEATURES } from "../constants";
import FallBackHandler from "../artifacts/FallBackHandler.json";
import SafeProxyFactory from "../artifacts/SafeProxyFactory.json";
import { getChainsConfig } from '@gnosis.pm/safe-react-gateway-sdk'
import { search } from "../utils/index";
import { EstimateSafeCreationGas } from "../utils/GasUtils";
import Web3 from "web3";
import wETH from "./../abis/wETH.json";
import PayLaterLoanCore from "../artifacts/PayLaterLoanCore.json";
import { NETWORK_MAP } from "../constants";
import { checksumAddress} from "../utils/index";
import { 
    EstimateTransactionGas, 
    generateERC721TransferTxData, 
    TransactionParameters, 
    getParametersStatus, 
    TX_NOTIFICATION_TYPES, 
    canExecuteCreatedTx, 
    getPreValidatedSignatures, 
    getExecutionTransaction,
    createSendParams
} from "../utils/GasUtils";
import { Operation } from '@gnosis.pm/safe-react-gateway-sdk'
import ERC721abi from "./../abis/ERC721";
import ERC1155abi from "./../abis/ERC1155.json";
import FeeController from "../artifacts/FeeController.json";
import PaylaterRepaymentController from "../artifacts/PayLaterRepaymentController.json";
import { ethers } from "ethers";
import WalletConnect from "@walletconnect/client";
import * as logo from "../assets/brand_logo/new_logo.jpg";
import { getRpcEngine } from '../engines/index';
import { apiGetCustomRequest } from '../helpers/api';

// Constants
export const ETH_STANDARD_PATH = "m/44'/60'/0'/0";
export const SUPPORTED_CHAINS = [
    {
      name: "Ethereum Mainnet",
      short_name: "eth",
      chain: "ETH",
      network: "mainnet",
      chain_id: 1,
      network_id: 1,
      rpc_url: "https://mainnet.infura.io/v3/%API_KEY%",
      native_currency: {
        symbol: "ETH",
        name: "Ether",
        decimals: "18",
        contractAddress: "",
        balance: "",
      },
    },
    {
      name: "Ethereum Ropsten",
      short_name: "rop",
      chain: "ETH",
      network: "ropsten",
      chain_id: 3,
      network_id: 3,
      rpc_url: "https://ropsten.infura.io/v3/%API_KEY%",
      native_currency: {
        symbol: "ETH",
        name: "Ether",
        decimals: "18",
        contractAddress: "",
        balance: "",
      },
    },
    {
      name: "Ethereum Rinkeby",
      short_name: "rin",
      chain: "ETH",
      network: "rinkeby",
      chain_id: 4,
      network_id: 4,
      rpc_url: "https://rinkeby.infura.io/v3/%API_KEY%",
      native_currency: {
        symbol: "ETH",
        name: "Ether",
        decimals: "18",
        contractAddress: "",
        balance: "",
      },
    },
    {
      name: "Ethereum Görli",
      short_name: "gor",
      chain: "ETH",
      network: "goerli",
      chain_id: 5,
      network_id: 5,
      rpc_url: "https://goerli.infura.io/v3/%API_KEY%",
      native_currency: {
        symbol: "ETH",
        name: "Ether",
        decimals: "18",
        contractAddress: "",
        balance: "",
      },
    },
    {
      name: "RSK Mainnet",
      short_name: "rsk",
      chain: "RSK",
      network: "mainnet",
      chain_id: 30,
      network_id: 30,
      rpc_url: "https://public-node.rsk.co",
      native_currency: {
        symbol: "R-BTC",
        name: "Smart Bitcoin",
        decimals: "18",
        contractAddress: "",
        balance: "",
      },
    },
    {
      name: "RSK Testnet",
      short_name: "trsk",
      chain: "tRSK",
      network: "testnet",
      chain_id: 31,
      network_id: 31,
      rpc_url: "https://public-node.testnet.rsk.co",
      native_currency: {
        symbol: "tR-BTC",
        name: "tSmart Bitcoin",
        decimals: "18",
        contractAddress: "",
        balance: "",
      },
    },
    {
      name: "Ethereum Kovan",
      short_name: "kov",
      chain: "ETH",
      network: "kovan",
      chain_id: 42,
      network_id: 42,
      rpc_url: "https://kovan.infura.io/v3/%API_KEY%",
      native_currency: {
        symbol: "ETH",
        name: "Ether",
        decimals: "18",
        contractAddress: "",
        balance: "",
      },
    },
    {
      name: "Ethereum Classic Mainnet",
      short_name: "etc",
      chain: "ETC",
      network: "mainnet",
      chain_id: 61,
      network_id: 1,
      rpc_url: "https://ethereumclassic.network",
      native_currency: {
        symbol: "ETH",
        name: "Ether Classic",
        decimals: "18",
        contractAddress: "",
        balance: "",
      },
    },
    {
      name: "POA Network Sokol",
      short_name: "poa",
      chain: "POA",
      network: "sokol",
      chain_id: 77,
      network_id: 77,
      rpc_url: "https://sokol.poa.network",
      native_currency: {
        symbol: "POA",
        name: "POA",
        decimals: "18",
        contractAddress: "",
        balance: "",
      },
    },
    {
      name: "POA Network Core",
      short_name: "skl",
      chain: "POA",
      network: "core",
      chain_id: 99,
      network_id: 99,
      rpc_url: "https://core.poa.network",
      native_currency: {
        symbol: "POA",
        name: "POA",
        decimals: "18",
        contractAddress: "",
        balance: "",
      },
    },
    {
      name: "xDAI Chain",
      short_name: "xdai",
      chain: "POA",
      network: "dai",
      chain_id: 100,
      network_id: 100,
      rpc_url: "https://dai.poa.network",
      native_currency: {
        symbol: "xDAI",
        name: "xDAI",
        decimals: "18",
        contractAddress: "",
        balance: "",
      },
    },
    {
      name: "Callisto Mainnet",
      short_name: "clo",
      chain: "callisto",
      network: "mainnet",
      chain_id: 820,
      network_id: 1,
      rpc_url: "https://clo-geth.0xinfra.com/",
      native_currency: {
        symbol: "CLO",
        name: "CLO",
        decimals: "18",
        contractAddress: "",
        balance: "",
      },
    },
    {
      name: "ARTIS sigma1",
      short_name: "artis-s1",
      chain: "ATS",
      network: "artis-s1",
      chain_id: 246529,
      network_id: 246529,
      rpc_url: "https://rpc.sigma1.artis.network",
      native_currency: {
        symbol: "ATS",
        name: "ATS",
        decimals: "18",
        contractAddress: "",
        balance: "",
      },
    },
    {
      name: "ARTIS tau1",
      short_name: "artis-t1",
      chain: "ATS",
      network: "artis-t1",
      chain_id: 246785,
      network_id: 246785,
      rpc_url: "https://rpc.tau1.artis.network",
      native_currency: {
        symbol: "ATS",
        name: "ATS",
        decimals: "18",
        contractAddress: "",
        balance: "",
      },
    },
    {
      name: "Eraswap Mainnet Network",
      short_name: "ESN",
      chain: "ESN",
      network: "mainnet",
      chain_id: 5197,
      network_id: 5197,
      rpc_url: "https://rpc-mumbai.mainnet.eraswap.network",
      native_currency: {
        symbol: "ES",
        name: "eraswap",
        decimals: "18",
        contractAddress: "",
        balance: "",
      },
    },
  ];
  
const appConfig = {
    name: "Deefy.co/wallet",
    logo: logo,
    chainId: "0x4",
    derivationPath: ETH_STANDARD_PATH,
    numberOfAccounts: 1,
    colors: {
      defaultColor: "12, 12, 13",
      backgroundColor: "40, 44, 52",
    },
    chains: SUPPORTED_CHAINS,
    styleOpts: {
      showPasteUri: true,
      showVersion: true,
    },
    rpcEngine: getRpcEngine(),
    events: {
      init: (state, setState) => Promise.resolve(),
      update: (state, setState) => Promise.resolve(),
    },
  };

// Redux and actions

const initialState = {
    financingWalletAddress: '',
    err: '',
    chains: [],
    balance: '',
    NFTbalance: [],
    approvalStatus: false,
    payLaterRequests: [],
    rentalRequests: [],

    // Wallet connect state
    loading: false,
    scanner: false,
    uri: '',
    peerMeta: [],
    connected: false,
    chainId: '',
    accounts: [],
    address: '',
    requests: [],
    payload: [],
    connector: [],
    connectedDapp: []

};

const slice = createSlice({
    name: 'wallet',
    initialState,
    reducers: {
        setFinancingWalletAddress(state, action) {
            const { financingWalletAddress } = action.payload;
            state.financingWalletAddress = financingWalletAddress;
        },
        setChainsData(state, action) {
            const { chains } = action.payload;
            state.chains = chains;
        },
        setBalances(state, action) {
            const { balance, NFTS } = action.payload;
            state.balance = balance;
            state.NFTbalance = NFTS;
        },
        setApprovalStatus(state, action) {
            const { ApprovalStatus } = action.payload;
            state.approvalStatus = ApprovalStatus;
        },
        setPayLaterRequests(state, action) {
            const { paylaterrequests } = action.payload;
            state.payLaterRequests = paylaterrequests
        },
        setRentalRequests(state, action) {
            const { rentalrequests } = action.payload;
            state.rentalRequests = rentalrequests
        },
        setConnectors(state, action) {
            const { connector } = action.payload;
            state.connector = connector;
        },
        setConnectedDapp(state, action) {
            const { connected, metadata } = action.payload;
            state.connected = connected;
            state.connectedDapp = metadata;
        },
        setRequests(state, action) {
            const { requests } = action.payload;
            state.requests.push(requests);
        },
        removeRequest(state, action) {
            const { request } = action.payload;
            console.log(request)
            state.requests = state.requests.filter((req) => req.id !== request.id);
        }
    }
});

export const reducer = slice.reducer;

// Actions

export const getFinancingWallet = (isAuthenticated, Moralis, account) => async (dispatch) => {
    try {
        if(isAuthenticated) {
            const FinancingWalletRecord = Moralis.Object.extend("Accounts");
            const query = new Moralis.Query(FinancingWalletRecord);
            query.equalTo("AssociatedAddress", account);
            query.equalTo("Network", NETWORK_MAP[Moralis?.chainId || "0x1"].name);
            const res = await query.find();

            const { results } = await getChainsConfig('https://safe-client.gnosis.io')
            

            dispatch(slice.actions.setChainsData({ chains: results}))
            if(res.length > 0) {

                const options = { chain: Moralis?.chainId, address: res[0].attributes.FinancingWalletAddress };
                const balance = await Moralis.Web3API.account.getNativeBalance(options);

                const opt = { chain: Moralis?.chainId, address: res[0].attributes.FinancingWalletAddress };
                const NFTs = await Moralis.Web3API.account.getNFTs(opt);


                dispatch(slice.actions.setBalances({balance: balance.balance, NFTS: NFTs.result}));
                dispatch(slice.actions.setFinancingWalletAddress({financingWalletAddress: res[0].attributes.FinancingWalletAddress}))
            } else {
                // do nothing
                dispatch(slice.actions.setBalances({balance: '', NFTS: []}));
                dispatch(slice.actions.setFinancingWalletAddress({financingWalletAddress: ''}))
            }
        }

    } catch (err) {
        console.log(err);
    }
};

export const CreateFinancingWallet = (account, provider,chains, currentChainId, Moralis) => async(dispatch) => {
    try {

        const web3Js = new Web3(provider);
        const SafeContract = new web3Js.eth.Contract(Safe.abi, Safe.defaultAddress, provider);
        const SafeProxyFactoryContract = new web3Js.eth.Contract(SafeProxyFactory.abi, SafeProxyFactory.defaultAddress, provider);
        const currentChain = search(currentChainId, chains)
        const safeAccounts = [account];
        const numOfConfirmations = 1;
        const SafeData = SafeContract.methods.setup(
            safeAccounts,
            numOfConfirmations,
            ZERO_ADDRESS,
            EMPTY_DATA,
            FallBackHandler.defaultAddress, 
            ZERO_ADDRESS,
            0,
            ZERO_ADDRESS,
            PayLaterLoanCore.defaultAddress
        ).encodeABI();


        const deploymentTX = SafeProxyFactoryContract.methods.createProxyWithNonce(Safe.defaultAddress, SafeData, SALT);
        const proxyFactoryData = deploymentTX.encodeABI();

        const { gasCostFormatted, gasLimit, gasPrice, gasMaxPrioFee } = await EstimateSafeCreationGas({
            addresses: safeAccounts,
            numOwners: numOfConfirmations,
            SALT,
            provider,
            proxyFactoryData,
            currentChain
        });


        const sendParams = createSendParams(account, {
            ethGasLimit: gasLimit.toString(),
            ethGasPriceInGWei: gasPrice,
            ethMaxPrioFeeInGWei: gasMaxPrioFee.toString(),
        },currentChain);

        deploymentTX.send(sendParams).once('transactionHash', (txHash) => {

        }).then(async(txReceipt) => {

            // resolve(txReceipt)
            if(txReceipt) {
                console.log(txReceipt)
                const Accounts = Moralis.Object.extend("Accounts");
                const AccountsInstance = new Accounts();
                AccountsInstance.set("AssociatedAddress", account)
                AccountsInstance.set("FinancingWalletAddress", txReceipt.events.ProxyCreation.returnValues.proxy)
                AccountsInstance.set("Network", NETWORK_MAP[Moralis?.chainId || "0x1"].name);
                await AccountsInstance.save();
                dispatch(slice.actions.setFinancingWalletAddress({financingWalletAddress: txReceipt.events.ProxyCreation.returnValues.proxy}))
            }
        })

    } catch (err) {
        console.log(err)
    }
};

export const CreatePayLaterRequest = (data, account, Moralis, ListingPrice, Interest, onClose) => async(dispatch) => {
    try {
        const PayLaterRequests = Moralis.Object.extend("PayLaterRequests");
        const PayLaterRequestsInstance = new PayLaterRequests();
        PayLaterRequestsInstance.set("lender", account);
        PayLaterRequestsInstance.set("NFT", data);
        PayLaterRequestsInstance.set("ListingPrice", ListingPrice);
        PayLaterRequestsInstance.set("LoanSanctioned", false)
        PayLaterRequestsInstance.set("Network", NETWORK_MAP[Moralis?.chainId || "0x1"].name);
        PayLaterRequestsInstance.set("repaid", false);
        PayLaterRequestsInstance.set("claimed", false);
        PayLaterRequestsInstance.set("LoanRepaymentTokenName", "wETH");
        PayLaterRequestsInstance.set("TokenId", data.token_id)
        PayLaterRequestsInstance.set("Interest", Interest)

        await PayLaterRequestsInstance.save();
        dispatch(getPayLaterRequests(Moralis))
        onClose()
    } catch (err) {
        console.log(err)
    }
};

export const CreateRentalRequest = (data, account, Moralis, DailyPrice, MaximumDays, onRentClose) => async(dispatch) => {
    try {
        const PayLaterRequests = Moralis.Object.extend("RentalRequests");
        const PayLaterRequestsInstance = new PayLaterRequests();
        PayLaterRequestsInstance.set("lender", account);
        PayLaterRequestsInstance.set("NFT", data);
        PayLaterRequestsInstance.set("DailyPrice", DailyPrice);
        PayLaterRequestsInstance.set("LoanSanctioned", false)
        PayLaterRequestsInstance.set("Network", NETWORK_MAP[Moralis?.chainId || "0x1"].name);
        PayLaterRequestsInstance.set("repaid", false);
        PayLaterRequestsInstance.set("claimed", false);
        PayLaterRequestsInstance.set("LoanRepaymentTokenName", "wETH");
        PayLaterRequestsInstance.set("TokenId", data.token_id)
        PayLaterRequestsInstance.set("MaximumDays", MaximumDays)

        await PayLaterRequestsInstance.save();
        dispatch(getRentalRequests(Moralis));
        onRentClose();
    } catch (err) {
        console.log(err)
    }
};

export const getPayLaterRequests = (Moralis) => async(dispatch) => {
    try {
        const PayLaterRequests = Moralis.Object.extend("PayLaterRequests");
        const query = new Moralis.Query(PayLaterRequests);
        query.equalTo("LoanSanctioned", false);
        query.descending("createdAt");
        const results = await query.find()
        console.log(results)
        dispatch(slice.actions.setPayLaterRequests({paylaterrequests: results}))
    } catch(err) {
        console.log(err)
    }
};

export const getRentalRequests = (Moralis) => async(dispatch) => {
    try {
        const PayLaterRequests = Moralis.Object.extend("RentalRequests");
        const query = new Moralis.Query(PayLaterRequests);
        query.equalTo("LoanSanctioned", false);
        query.descending("createdAt");
        const results = await query.find()
        console.log(results)
        dispatch(slice.actions.setRentalRequests({rentalrequests: results}))
    } catch(err) {
        console.log(err)
    }
};

export const getApprovalForPayLaterTransfer = (data, Moralis, account, isAuthenticated, ListingPrice, Interest, onClose) => async(dispatch) => {
    try {
        if(account && isAuthenticated) {
            const web3Js = new Web3(Moralis.provider);
            if( data.contract_type === "ERC721" ) {
                const nftContract = new web3Js.eth.Contract(ERC721abi.abi, data.token_address, Moralis.provider);
                await nftContract.methods
                .approve(OriginationManager.defaultAddress, data.token_id)
                .send({ from: account }).then(async(result) => {
                if(isAuthenticated) dispatch(CreatePayLaterRequest(data, account, Moralis, ListingPrice, Interest, onClose))
                if(isAuthenticated) dispatch(slice.actions.setApprovalStatus({ApprovalStatus: true }))
                
                })
            } else if ( data.contract_type === "ERC1155" ) {
                const nftContract = new web3Js.eth.Contract(ERC1155abi.abi, data.token_address, Moralis.provider);
                await nftContract.methods
                .approve(OriginationManager.defaultAddress, data.token_id)
                .send({ from: account }).then(async(result) => {
                if(isAuthenticated) dispatch(CreatePayLaterRequest(data, account, Moralis, ListingPrice, Interest, onClose))
                if(isAuthenticated) dispatch(slice.actions.setApprovalStatus({ApprovalStatus: true }))
                })
            }
            dispatch(slice.actions.setApprovalStatus({ApprovalStatus: false }))

        }
    } catch(err) {
        console.log(err)
    }
};

export const getApprovalForRentalTransfer = (data, Moralis, account, isAuthenticated, DailyPrice, MaxDays, onRentClose) => async(dispatch) => {
    try {
        if(account && isAuthenticated) {
            const web3Js = new Web3(Moralis.provider);
            if( data.contract_type === "ERC721" ) {
                const nftContract = new web3Js.eth.Contract(ERC721abi.abi, data.token_address, Moralis.provider);
                await nftContract.methods
                .approve(OriginationManager.defaultAddress, data.token_id)
                .send({ from: account }).then(async(result) => {
                if(isAuthenticated) dispatch(CreateRentalRequest(data, account, Moralis, DailyPrice, MaxDays, onRentClose))
                if(isAuthenticated) dispatch(slice.actions.setApprovalStatus({ApprovalStatus: true }))
                
                })
            } else if ( data.contract_type === "ERC1155" ) {
                const nftContract = new web3Js.eth.Contract(ERC1155abi.abi, data.token_address, Moralis.provider);
                await nftContract.methods
                .approve(OriginationManager.defaultAddress, data.token_id)
                .send({ from: account }).then(async(result) => {
                if(isAuthenticated) dispatch(CreateRentalRequest(data, account, Moralis, DailyPrice, MaxDays, onRentClose))
                if(isAuthenticated) dispatch(slice.actions.setApprovalStatus({ApprovalStatus: true }))
                })
            }
            dispatch(slice.actions.setApprovalStatus({ApprovalStatus: false }))

        }
    } catch(err) {
        console.log(err)
    }
};

export const BuyPayLaterNFT = (Moralis, NFT, financingWalletAddress, account, ListingPrice, lender, requestId, terms, onClose, getRequests) => async(dispatch) => {
    try {
        if(financingWalletAddress && account && lender) {
            const ethers = Moralis.web3Library;
            console.log(terms)
            const loanTerms = {
                // The number of seconds representing relative due date of the loan
                durationSecs: parseInt(terms.loanDuration),
                // The amount of principal in terms of the payableCurrency
                principal: ethers.utils.parseEther(ListingPrice),
                // The amount of interest in terms of the payableCurrency
                interest: terms.rateOfInterest,
                // The tokenID of the collateral bundle
                collateralTokenId: NFT.token_id,
                // The payable currency for the loan principal and interest
                payableCurrency: wETH.address,
                // The number parts in the payment cycle
                parts: terms.installments,
                // The part duration in seconds
                partduration: parseInt(parseInt(terms.loanDuration) / terms.installments)
            };
            console.log(loanTerms)
            const PayLaterRequests = Moralis.Object.extend("PayLaterRequests");
            const PayLaterRequestsInstance = new PayLaterRequests();
            PayLaterRequestsInstance.id = requestId;
            PayLaterRequestsInstance.set("PayLaterTermsAccepted", loanTerms)
            PayLaterRequestsInstance.set("Borrower", account)
            PayLaterRequestsInstance.set("BorrowerSecondaryWalletAddress", financingWalletAddress)
            PayLaterRequestsInstance.set("TokenAddress", NFT.token_address)
            // PayLaterRequestsInstance.set("TokenId", NFT.token_id)
            PayLaterRequestsInstance.set("LoanRepaymentAmount", terms.repaymentAmount)

            const tokenType = NFT.contract_type === "ERC721" ? 0 : (NFT.contract_type === "ERC1155"? 1 : 3);
            const web3Js = new Web3(Moralis.provider)
            const wETHContract = new web3Js.eth.Contract(wETH.abi, wETH.address);
            const PayLateCore = new web3Js.eth.Contract(PayLaterLoanCore.abi, PayLaterLoanCore.defaultAddress);
            const ins = await PayLateCore.methods.getFirstInstallmentWithoutLoan(loanTerms.principal, loanTerms.parts, loanTerms.interest).call({ from: account})
            await wETHContract.methods.approve(OriginationManager.defaultAddress, ins).send({ from: account})
            const OriginationManagerInstance = new web3Js.eth.Contract(OriginationManager.abi, OriginationManager.defaultAddress);
            const response = await OriginationManagerInstance.methods.initializePayLaterRequest(
                loanTerms,
                account,
                financingWalletAddress,
                lender,
                tokenType,
                NFT.token_address
            ).send({ from: account });
            
            console.log(response);

            await PayLateCore.once("LoanStarted", (error, event) => {
                console.log(error, event, "From loanId")
            })

            const events = await PayLateCore.getPastEvents("allEvents")
            console.log(events)
            PayLaterRequestsInstance.set("LoanId", events[0].returnValues.loanId)
            PayLaterRequestsInstance.set("LoanSanctioned", true)

            await PayLaterRequestsInstance.save();
            onClose();
            getRequests();
        }

    } catch (err) {
        console.log(err);
    }
}

export const startRental = (
    Moralis, 
    NFT, 
    financingWalletAddress, 
    account, 
    lender, 
    requestId, 
    loanTerms, 
    onClose, 
    getRequests
) => async (dispatch) => {

    if(financingWalletAddress && account && lender) {
        const terms = {
            // The number of seconds representing relative due date of the loan
            durationSecs: parseInt(loanTerms.loanDuration),
            // The amount of principal in terms of the payableCurrency
            principal: ethers.utils.parseEther(`${loanTerms.loanAmount}`),
            
            // The tokenID of the collateral bundle
            collateralTokenId: NFT.token_id,
            // The payable currency for the loan principal and interest
            payableCurrency: wETH.address,
            // The number parts in the payment cycle
            parts: loanTerms.installments,
            // The part duration in seconds
            partduration: parseInt(parseInt(loanTerms.loanDuration) / loanTerms.installments)
        };
        const tokenType = NFT.contract_type === "ERC721" ? 0 : (NFT.contract_type === "ERC1155"? 1 : 3);
        const rentalType = loanTerms.installments == 1 ? 0 : 1;

        const RentalRequests = Moralis.Object.extend("RentalRequests");
        const RentalRequestsInstance = new RentalRequests();
        RentalRequestsInstance.id = requestId;
        RentalRequestsInstance.set("RentalTermsAccepted", terms)
        RentalRequestsInstance.set("Borrower", account)
        RentalRequestsInstance.set("BorrowerSecondaryWalletAddress", financingWalletAddress)
        RentalRequestsInstance.set("TokenAddress", NFT.token_address)
        // PayLaterRequestsInstance.set("TokenId", NFT.token_id)
        RentalRequestsInstance.set("LoanRepaymentAmount", loanTerms.repaymentAmount)

        const web3Js = new Web3(Moralis.provider)
        const wETHContract = new web3Js.eth.Contract(wETH.abi, wETH.address);
        const FeeControllerContract = new web3Js.eth.Contract(FeeController.abi, FeeController.defaultAddress);
        const PayLateCore = new web3Js.eth.Contract(PayLaterLoanCore.abi, PayLaterLoanCore.defaultAddress);

        // const firstInstallment = (parseFloat(loanTerms.loanAmount) / parseFloat(terms.parts)) + ((parseFloat(loanTerms.loanAmount) / parseFloat(terms.parts)) * (parseFloat(platformFee) / 10000))
        // console.log(firstInstallment);

        // getFirstRentalInstallmentWithoutLoan
        const ins = await PayLateCore.methods.getFirstRentalInstallmentWithoutLoan(terms.principal, terms.parts).call({ from: account})

        await wETHContract.methods.approve(OriginationManager.defaultAddress, ins).send({ from: account})
        const OriginationManagerInstance = new web3Js.eth.Contract(OriginationManager.abi, OriginationManager.defaultAddress);
        const response = await OriginationManagerInstance.methods.initializeRentalRequest(
            terms,
            account,
            financingWalletAddress,
            lender,
            tokenType,
            NFT.token_address, 
            rentalType
        ).send({ from: account });
        
        console.log(response);
        const events = await PayLateCore.getPastEvents("allEvents")
        console.log(events)
        RentalRequestsInstance.set("LoanId", events[0]?.returnValues?.loanId)
        RentalRequestsInstance.set("LoanSanctioned", true)
        if(events[1]?.event == "RentRepaid" && events[0]?.returnValues?.loanId == events[1].returnValues.loanId) {
            RentalRequestsInstance.set("repaid", true)
        }
        await RentalRequestsInstance.save();
        onClose();
        getRequests();

    }
}

export const getLoanDetailsByLoanId = async(loanId, Moralis, account) => {
    try {
        const web3Js = new Web3(Moralis.provider);
        const PayLateCore = new web3Js.eth.Contract(PayLaterLoanCore.abi, PayLaterLoanCore.defaultAddress);
        return await PayLateCore.methods.getInstallmentOfPayLaterPurchase(parseInt(loanId)).call({ from: account})
    } catch (err) {
        console.log(err);
    }
}

export const getRentalDetailsByLoanId = async(loanId, Moralis, account) => {
    try {
        const web3Js = new Web3(Moralis.provider);
        const PayLateCore = new web3Js.eth.Contract(PayLaterLoanCore.abi, PayLaterLoanCore.defaultAddress);
        return await PayLateCore.methods.getInstallmentOfRentalPurchase(parseInt(loanId)).call({ from: account})
    } catch (err) {
        console.log(err);
    }
}

export const getGracePeriod= async(Moralis, account) => {
    try {
        const web3Js = new Web3(Moralis.provider);
        const FeeContollerInstance = new web3Js.eth.Contract(FeeController.abi, FeeController.defaultAddress);
        return await FeeContollerInstance.methods.getGracePeriod().call({ from: account})
    } catch (err) {
        console.log(err);
    }
}

export const repayPayLaterLoan = async(loanId, Moralis, account, id, getData) => {

    const PayLaterRequests = Moralis.Object.extend("PayLaterRequests");
    const PayLaterRequestsInstance = new PayLaterRequests();
    PayLaterRequestsInstance.id = id;

    const pendingInstallment = await getLoanDetailsByLoanId(loanId, Moralis, account);
    const web3Js = new Web3(Moralis.provider);
    const repaymentController = new web3Js.eth.Contract(PaylaterRepaymentController.abi, PaylaterRepaymentController.defaultAddress);
    const wETHContract = new web3Js.eth.Contract(wETH.abi, wETH.address);
    await wETHContract.methods.approve(PaylaterRepaymentController.defaultAddress, ethers.utils.parseEther(`${pendingInstallment[0]}`)).send({ from: account})
    await repaymentController.methods.repay(parseInt(loanId)).send({ from: account})
    const PayLateCore = new web3Js.eth.Contract(PayLaterLoanCore.abi, PayLaterLoanCore.defaultAddress);
    const events = await PayLateCore.getPastEvents("allEvents")
    if(events[0].event === "LoanRepaid") {
        if(events[0].returnValues.loanId == loanId) {
            PayLaterRequestsInstance.set("repaid", true)
            await PayLaterRequestsInstance.save();

        }
    }
    getData()
}   

export const claimPayLaterLoan = async(loanId, Moralis, account, id, getData) => {
    const PayLaterRequests = Moralis.Object.extend("PayLaterRequests");
    const PayLaterRequestsInstance = new PayLaterRequests();
    PayLaterRequestsInstance.id = id;
    const web3Js = new Web3(Moralis.provider);
    const repaymentController = new web3Js.eth.Contract(PaylaterRepaymentController.abi, PaylaterRepaymentController.defaultAddress);
    await repaymentController.methods.claim(parseInt(loanId)).send({ from: account})
    const PayLateCore = new web3Js.eth.Contract(PayLaterLoanCore.abi, PayLaterLoanCore.defaultAddress);
    const events = await PayLateCore.getPastEvents("allEvents")
    if(events[0].event === "LoanClaimed") {
        if(events[0].returnValues.loanId == loanId) {
            PayLaterRequestsInstance.set("claimed", true)
            await PayLaterRequestsInstance.save();

        }
    }
    getData()
}

export const claimRentalAsset = async(loanId, Moralis, account, id, getData) => {
    const PayLaterRequests = Moralis.Object.extend("RentalRequests");
    const PayLaterRequestsInstance = new PayLaterRequests();
    PayLaterRequestsInstance.id = id;
    const web3Js = new Web3(Moralis.provider);
    const repaymentController = new web3Js.eth.Contract(PaylaterRepaymentController.abi, PaylaterRepaymentController.defaultAddress);
    await repaymentController.methods.claimRental(parseInt(loanId)).send({ from: account})
    const PayLateCore = new web3Js.eth.Contract(PayLaterLoanCore.abi, PayLaterLoanCore.defaultAddress);
    const events = await PayLateCore.getPastEvents("allEvents")
    if(events[0].event === "RentalAssetClaimed") {
        if(events[0].returnValues.loanId == loanId) {
            PayLaterRequestsInstance.set("claimed", true)
            await PayLaterRequestsInstance.save();

        }
    }
    getData()
}

export const transferNFTFromFinancingWallet = async (
    NFT, 
    Moralis, 
    account, 
    provider, 
    chains, 
    safeAddress,
    recipientAddress
    ) => {
    try {
        const web3Js = new Web3(Moralis.provider)
        const currentChainId = Moralis?.chainId.split("0x")[1];
        const currentChain = search(currentChainId, chains);
        const SafeNonce = await web3Js.eth.getTransactionCount(safeAddress, 'pending');
        const tx = {
            token_address: NFT.token_address,
            assetName: NFT.name,
            recipientAddress: recipientAddress,
            token_id: NFT.token_id
        };
        const txData = await generateERC721TransferTxData(tx, safeAddress, currentChainId, provider);

        const {
            gasCostFormatted,
            gasPriceFormatted,
            gasMaxPrioFeeFormatted,
            gasLimit,
            gasEstimation,
            txEstimationExecutionStatus,
            isCreation,
            isOffChainSignature,
            canTxExecute
        } = await EstimateTransactionGas({
            txData,
            txRecipient: NFT.token_address || safeAddress,
            txType: undefined,
            txConfirmations: undefined,
            txAmount: "0",
            preApprovingOwner: account,
            safeTxGas: "0",
            manualGasPrice: undefined,
            manualMaxPrioFee: undefined,
            manualGasLimit: undefined,
            manualSafeNonce: undefined,
            operation: undefined,
            currentChain,
            currentChainId,
            provider,
            safeAddress,
            account,
            SafeNonce
        });

        const defaultParameterStatus = canTxExecute ? 'ENABLED' : 'ETH_HIDDEN'

        const parametersStatus = getParametersStatus(isCreation, canTxExecute, undefined)

        const txParameters = await TransactionParameters({
            parametersStatus: parametersStatus || defaultParameterStatus,
            initialEthGasLimit: gasLimit,
            initialEthGasPrice: gasPriceFormatted,
            initialEthMaxPrioFee: gasMaxPrioFeeFormatted,
            initialSafeNonce: SafeNonce,
            initialSafeTxGas: gasEstimation,
            provider: provider,
            connectedWalletAddress: account,
            safeAddress: safeAddress,
            currentChainId: currentChainId
        })

        const txProps = {
            safeAddress,
            to: NFT.token_address,
            valueInWei: '0',
            txNonce: SafeNonce,
            safeTxGas: txParameters.safeTxGas,
            ethParameters: txParameters,
            notifiedTransaction: TX_NOTIFICATION_TYPES.STANDARD_TX,
            delayExecution: false,
            operation: Operation.CALL,
            origin: null,
            txData: txData
        };

        const ethAdapter = new Web3(provider);
  
        const safeInstance = new ethAdapter.eth.Contract(Safe.abi, safeAddress, provider);

        const isFinalization = await canExecuteCreatedTx(safeInstance, SafeNonce);

        const txArgs = {
            safeInstance: safeInstance,
            to: txProps.to,
            valueInWei: txProps.valueInWei,
            data: txProps.txData,
            operation: txProps.operation,
            nonce: Number.parseInt(SafeNonce),
            safeTxGas: txProps.safeTxGas,
            baseGas: '0',
            gasPrice: '0',
            gasToken: ZERO_ADDRESS,
            refundReceiver: ZERO_ADDRESS,
            sender: account,
            // We're making a new tx, so there are no other signatures
            // Just pass our own address for an unsigned execution
            // Contract will compare the sender address to this
            sigs: getPreValidatedSignatures(account),
            tokenId: NFT.token_id
        }
        const safeVersion = "1.3.0";
    
        // const safeTxHash = generateSafeTxHash(txProps.safeAddress, safeVersion, txArgs, currentChainId)
        // const txx = isFinalization ? getExecutionTransaction(txArgs) : getApprovalTransaction(safeInstance, safeTxHash)
        // // await getApprovalTransaction(safeInstance, safeTxHash).send({ from: account})
        // const sendParams = createSendParams(account, txProps.ethParameters || {}, currentChain)
        const promiEvent = await getExecutionTransaction(txArgs).send({ from: account})
        const result = new Promise((resolve, reject) => {
            promiEvent.once('transactionHash', resolve) // this happens much faster than receipt
            promiEvent.once('error', reject)
        })
        // const re = await result
        // console.log(re)
        
        
        // Promise.all([result, onChainError]).then((values) => console.log)
        await result
        console.log(await safeInstance.getPastEvents("allEvents"))

    } catch (err) {
        console.log(err)
        const ethAdapter = new Web3(provider);
  
        const safeInstance = new ethAdapter.eth.Contract(Safe.abi, safeAddress);
        console.log(await safeInstance.getPastEvents("allEvents"))

        return
    }
};

export const initWalletConnect = (uri, financingWalletAddress) => async(dispatch) => {
    try {
        await window.localStorage.removeItem('walletconnect')
        const connector = new WalletConnect({ uri });

        if (!connector.connected) {
            await connector.createSession();
        }
        dispatch(slice.actions.setConnectors({connector}));
        
        connector.on("session_request", async(error, payload) => {
            console.log("EVENT", "session_request", payload);
    
            if (error) {
              throw error;
            }
            dispatch(slice.actions.setRequests({requests: payload}))
            
        });

        connector.on("session_update", error => {
            console.log("EVENT", "session_update");
    
            if (error) {
              throw error;
            }
        });

        connector.on("call_request", async (error, payload) => {
            // tslint:disable-next-line
            console.log("EVENT", "call_request", "method", payload.method);
            console.log("EVENT", "call_request", "params", payload.params);
            console.log("EVENT", "call_request", "Payload", payload);
            dispatch(slice.actions.setRequests({requests: payload}))
    
            if (error) {
              throw error;
            }

        });

        connector.on("connect", (error, payload) => {
            console.log("EVENT", "connect", payload);
    
            if (error) {
              throw error;
            }
            dispatch(slice.actions.setConnectedDapp({ connected: true, metadata: payload.params[0]}));
        });

        connector.on("disconnect", (error, payload) => {
            console.log("EVENT", "disconnect", payload);


            if (error) {
                throw error;
            }
            dispatch(slice.actions.setConnectedDapp({ connected: false, metadata: []}));
            dispatch(slice.actions.setConnectors({connector: []})); 
        });
        

        connector.on("wc_sessionUpdate", (error, payload) => {
            console.log("EVENT", "wc_sessionUpdate", payload);


            if (error) {
                throw error;
            }

            // this.resetApp();
        });

    } catch (err) {
        console.log(err)
    }
};

export const disconnectFromDapp = (connector) => (dispatch) => {
    try {
        if (connector) {
            connector.killSession();
        }
    } catch (err) {
        console.log(err)
    }
}

export const approveRequest = (connector, financingWalletAddress, request) => async (dispatch) => {
    try {

        await connector.approveSession({ chainId: 4, accounts: [`${financingWalletAddress}`]})
        dispatch(slice.actions.removeRequest({request: request}));
    } catch (err) {
        console.log(err)
    }
};

export const rejectRequest = (connector, request) => async (dispatch) => {
    try {

        await connector.rejectSession({ message: 'User Rejected' })
        dispatch(slice.actions.removeRequest({request: request}));

    } catch (err) {
        console.log(err)
    }
};

export const rejectActiveRequest = (connector, request) => async (dispatch) => {
    try {

        await connector.rejectRequest({ message: 'User Rejected' })
        dispatch(slice.actions.removeRequest({id: request.id, error: {message: 'User Rejected'}}));

    } catch (err) {
        console.log(err)
    }
};

export const signPersonalMessage = (connector, message, request, Moralis) => {
    try {
        // const web3Js = new Web3(Moralis.provider)
        // const txData = getSignMessageLibContractInstance(web3, networkId)
        // .methods.signMessage(web3.eth.accounts.hashMessage(message))
        // .encodeABI()
    } catch (err) {
        console.log(err);
    }
}